import React, { useEffect, useMemo, useState } from 'react';
import Heading from '../../../../../../../common/element/Heading';
import Button from '../../../../../../../common/element/Buttons';
import { setBreadcrumbs } from '../../../../../../../redux/Admin/breadcrumbsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { setEmployeeName, resetEmployeeName } from '../../../../../../../redux/Admin/employeeViewSlice';
import OverViewEmployee from './OverViewEmployee';
import SessionList from './SessionList';
import { useLocation } from 'react-router-dom';
import EmployeeSessionView from './EmployeeSessionView';
import Dialog from '../../../../../../../common/element/Dialog';
import BtnPrimary from '../../../../../../../common/element/Buttons/btnPrimary';
import {
  clearEmployeeDetail,
  getEmployeeDetail,
  getCompanyEmployeeInfo,
  clearCompanyEmployeeInfo
} from '../../../../../../../redux/Employee/employeesSlice';
import { lockCompanyEmployee } from '../../../../../../../redux/Employee/employeesSlice';
import {
  selectEmployeeDetail,
  selectEmployeeDetailLoading,
  selectCompanyEmployeeInfo,
  selectCompanyEmployeeInfoLoading
} from '../../../../../../../redux/Employee/employeesSelector';
import { selectLockCompanyEmployeeLoading } from '../../../../../../../redux/Employee/employeesSelector';
import SweetAlertService from '../../../../../../../common/SweetAlert/SweetAlert';
import { CompanyAvatar } from '../../../../../../../common/element/CompanyAvatar/CompanyAvatar';
import ImagePreviewModal from '../../../../../../../common/ImagePreviewModal/ImagePreviewModal';

const EmployeeView = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  // ✅ NEW: State for the Image Preview Modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    url: "",
    alt: "",
  });

  // Existing selectors
  const employeeDetail = useSelector(selectEmployeeDetail);
  const employeeDetailLoading = useSelector(selectEmployeeDetailLoading);
  const storedCompanyName = useSelector((state) => state.companyView?.companyName);
  const storedEmployeeName = useSelector((state) => state.employeeView?.employeeName);
  const storedSessionName = useSelector((state) => state.sessionView?.sessionName);
  console.log(storedSessionName, "storedSessionNamestoredSessionName")
  // ✅ NEW: Company Employee Info selectors
  const companyEmployeeInfo = useSelector(selectCompanyEmployeeInfo);
  const companyEmployeeInfoLoading = useSelector(selectCompanyEmployeeInfoLoading);
  const lockLoading = useSelector(selectLockCompanyEmployeeLoading);

  const searchParams = new URLSearchParams(location.search);
  const EmployeeView = searchParams.get('employeeId');
  const tab = searchParams.get("tab");
  const companyId = searchParams.get("companyId");
  const sessionView = searchParams.get('sessionView');
  const sessionId = searchParams.get('sessionId');
  const sessionName = searchParams.get('sessionName');

  // Removed hard-coded dummy data (coachData). Use API responses only.

  // ✅ Fetch employee detail and company employee info
  useEffect(() => {
    if (EmployeeView) {
      // Fetch existing employee detail
      dispatch(getEmployeeDetail({ companyId, employeeId: EmployeeView }));
      dispatch(getCompanyEmployeeInfo({ companyEmployeeId: EmployeeView }));
    }

    return () => {
      dispatch(clearEmployeeDetail());
      dispatch(clearCompanyEmployeeInfo()); // ✅ NEW: Clear on unmount
    };
  }, [EmployeeView, companyId, dispatch]);

  // ✅ Updated displayData to merge both API responses
  const displayData = useMemo(() => {
    const detail = employeeDetail?.data || employeeDetail || {};
    const companyInfo = companyEmployeeInfo?.data || companyEmployeeInfo || {};
    console.log(companyEmployeeInfo, "companyEmployeeInfo?.datacompanyEmployeeInfo?.data");
    return {
      ...detail,
      ...companyInfo,
      name: companyInfo?.employee_name || detail?.name || detail?.employeeName || '',
      status: companyInfo?.status || detail?.status || '',
      phoneNumber: companyInfo?.employee_mobile_number || detail?.phoneNumber || detail?.phone || detail?.mobile || '',
      emailAddress: companyInfo?.email || detail?.email || detail?.emailAddress || '',
      companyName: companyInfo?.company_name || detail?.companyName || '',
      isActive: companyInfo?.isActive ? 'Active' : 'Inactive',
    };
  }, [employeeDetail, companyEmployeeInfo]);

  // ✅ Breadcrumbs
  useEffect(() => {
    // prefer persisted employee name so refresh shows label immediately
    const companyLabel = displayData?.companyName || storedCompanyName || 'View Company';

    const employeeLabel = storedEmployeeName || displayData?.name || 'Employee View';
    const sessionLabel = storedSessionName || sessionName || displayData?.sessionName || 'Session View';
    console.log(storedSessionName, "storedSessionNamestoredSessionName")
    const crumbs = [
      { label: "Companies", url: "/companies", home: "/companies" },
      { label: companyLabel, url: `/companies/companyview?companyId=${companyId}&tab=${tab}` },
      { label: employeeLabel, url: `/companies/companyview?companyId=${companyId}&tab=${tab}&employeeId=${EmployeeView}` },
    ];

    if (sessionView) {
      crumbs.push({
        label: sessionLabel,
        url: `/companies/companyview?companyId=${companyId}&tab=${tab}&employeeId=${EmployeeView}&sessionView=${sessionView}&sessionId=${sessionId}`,
      });
    }

    dispatch(setBreadcrumbs(crumbs));
  }, [companyId, tab, EmployeeView, sessionView, sessionName, dispatch, storedEmployeeName, storedSessionName, displayData?.name, displayData?.sessionName]);

  // persist employee name when displayData changes
  useEffect(() => {
    if (displayData?.name) {
      dispatch(setEmployeeName(displayData.name));
    }
  }, [displayData, dispatch]);

  // clear employee name on unmount (but not on full page reload)
  useEffect(() => {
    const isReload = { current: false };
    const onBeforeUnload = () => { isReload.current = true; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (!isReload.current) dispatch(resetEmployeeName());
    };
  }, [dispatch]);

  // ✅ Keep tab synced
  useEffect(() => {
    if (sessionView === "view") {
      setActiveTab('sessions');
    }
  }, [sessionView]);

  // ✅ Dialog Handlers
  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);
  // Confirm handler: toggle status via API and refresh company info
  const handleConfirmLock = async () => {
    // Determine current status from displayData and toggle
    const currentStatus = displayData?.employee_status || displayData?.status || '';
    // use 'lock'/'unlock' values per requirement
    const newStatus = currentStatus === 'unlock' ? 'lock' : 'unlock';

    try {
      // call API
      const data = await dispatch(lockCompanyEmployee({ company_employee_id: Number(EmployeeView), status: newStatus })).unwrap();
      // Show success alert with API message (or fallback)
      const successMessage = 'Employee status updated successfully';
      SweetAlertService.showSuccess({
        title: 'Success!',
        message: (data && data.message) || successMessage,
        onConfirm: () => {
          // Refresh company employee info after user confirms the alert
          dispatch(getCompanyEmployeeInfo({ companyEmployeeId: EmployeeView }));
          // close the confirmation dialog if still open
          handleDialogClose();
        }
      });
    } catch (err) {
      // Show API error message if available
      const errorMessage = err?.message || err?.data?.message || (err && err.toString()) || 'Something went wrong';
      SweetAlertService.showError({
        title: 'Error!',
        message: errorMessage
      });
      console.error('Failed to toggle lock status', err);
    } finally {
      handleDialogClose();
    }
  };

  // ✅ NEW: Image Preview Handlers
  const openImagePreview = () => {
    if (!displayData?.profile_image) return;
    setImageModal({ 
      isOpen: true, 
      url: displayData.profile_image, 
      alt: displayData.name || "Employee Profile" 
    });
  };

  const closeImagePreview = () => {
    setImageModal({ isOpen: false, url: "", alt: "" });
  };

  console.log(displayData, "displayDatadisplayDatadisplayData");
  return (
    <div>
      <div className="flex justify-between mb-5">
        <div className="w-full">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              
              {/* ✅ NEW: Wrap CompanyAvatar in clickable div */}
              <div 
                onClick={openImagePreview}
                className={displayData?.profile_image ? "cursor-pointer hover:opacity-80 transition" : ""}
                title={displayData?.profile_image ? "View Profile Image" : ""}
              >
                <CompanyAvatar logo={displayData?.profile_image} name={displayData.name} />
              </div>

              <div>
                <div className="w-80 h-10 text-start">
                  <Heading>{displayData.name}</Heading>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <div className={`flex items-center gap-1 px-3 py-0.5 ${displayData.isActive === 'Active' ? 'bg-[#ECFDF3]' : 'bg-slate-100'} rounded-[16px]`}>
                    <div className={`w-2 h-2 ${displayData.isActive === 'Active' ? 'bg-green-500' : 'bg-red-500'} rounded-full`}></div>
                    <span className={`font-lato font-medium text-[14px] ${displayData.isActive === 'Active' ? 'text-[#027A48]' : 'text-[#slate-700]'}`}>
                      {displayData.isActive}
                    </span>
                  </div>
                   <span className="font-lato font-medium text-[14px] text-[#1D2939] flex items-center gap-2">
                      
                    <span>{displayData.employee_email}</span> •  <span>{displayData.phoneNumber}</span>
                    </span>
                  
                </div>
              </div>
            </div>

            {/* ✅ Lock/Unlock Button */}
            {sessionView !== "view" && (
              <div>
                <Button
                  onClick={handleDialogOpen}
                  className={`border flex items-center justify-center rounded-lg h-9 text-white text-sm font-semibold duration-150 gap-1 px-5 ${(displayData?.employee_status === 'lock')
                      ? 'bg-[#22C55E] border-[#22C55E] hover:bg-[#16A34A]'
                      : 'bg-[#FF8E1C] border-[#FF8E1C] hover:bg-[#e57d12]'
                    }`}
                >
                  {/* Per requirement: show 'Unlock' when employee_status is 'unlocked', otherwise show 'Lock' */}
                  {displayData?.employee_status === 'unlock' ? 'Lock' : 'Unlock'}
                  {displayData?.employee_status === 'lock' ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.0625 8.00003H5.34375V4.77816C5.34375 3.54066 6.4582 2.51253 7.85039 2.50003C9.25664 2.48753 10.4062 3.50316 10.4062 4.75003V5.25003C10.4062 5.66566 10.7824 6.00003 11.25 6.00003H12.375C12.8426 6.00003 13.2188 5.66566 13.2188 5.25003V4.75003C13.2188 2.12503 10.8105 -0.00934423 7.85742 3.07662e-05C4.9043 0.00940577 2.53125 2.17191 2.53125 4.79691V8.00003H1.6875C0.755859 8.00003 0 8.67191 0 9.50003V14.5C0 15.3282 0.755859 16 1.6875 16H14.0625C14.9941 16 15.75 15.3282 15.75 14.5V9.50003C15.75 8.67191 14.9941 8.00003 14.0625 8.00003ZM9.28125 12.75C9.28125 13.4407 8.65195 14 7.875 14C7.09805 14 6.46875 13.4407 6.46875 12.75V11.25C6.46875 10.5594 7.09805 10 7.875 10C8.65195 10 9.28125 10.5594 9.28125 11.25V12.75Z" fill="white" />
                    </svg>
                  ) : (
                    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.5 7H11.75V4.75C11.75 2.13125 9.61875 0 7 0C4.38125 0 2.25 2.13125 2.25 4.75V7H1.5C0.671875 7 0 7.67188 0 8.5V14.5C0 15.3281 0.671875 16 1.5 16H12.5C13.3281 16 14 15.3281 14 14.5V8.5C14 7.67188 13.3281 7 12.5 7ZM9.25 7H4.75V4.75C4.75 3.50938 5.75938 2.5 7 2.5C8.24063 2.5 9.25 3.50938 9.25 4.75V7Z" fill="white" />
                    </svg>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* ✅ Confirmation Dialog */}
          <Dialog isOpen={dialogOpen} onClose={handleDialogClose} width={380}>
            <div className="flex flex-col items-center justify-center py-8 px-6">
              <div className="bg-[#FF8E1C] w-[60px] h-[60px] rounded-full mb-4 flex items-center justify-center">
                {/* show an icon - keep original lock icon */}
                <svg width="24"  viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.0625 8.00003H5.34375V4.77816C5.34375 3.54066 6.4582 2.51253 7.85039 2.50003C9.25664 2.48753 10.4062 3.50316 10.4062 4.75003V5.25003C10.4062 5.66566 10.7824 6.00003 11.25 6.00003H12.375C12.8426 6.00003 13.2188 5.66566 13.2188 5.25003V4.75003C13.2188 2.12503 10.8105 -0.00934423 7.85742 3.07662e-05C4.9043 0.00940577 2.53125 2.17191 2.53125 4.79691V8.00003H1.6875C0.755859 8.00003 0 8.67191 0 9.50003V14.5C0 15.3282 0.755859 16 1.6875 16H14.0625C14.9941 16 15.75 15.3282 15.75 14.5V9.50003C15.75 8.67191 14.9941 8.00003 14.0625 8.00003ZM9.28125 12.75C9.28125 13.4407 8.65195 14 7.875 14C7.09805 14 6.46875 13.4407 6.46875 12.75V11.25C6.46875 10.5594 7.09805 10 7.875 10C8.65195 10 9.28125 10.5594 9.28125 11.25V12.75Z" fill="white" />
                </svg>

              </div>

              <h3 className="custom-message mb-6 text-center">
                {/* Action will toggle the current status; show confirmation accordingly */}
                {displayData?.employee_status === 'unlock'
                  ? 'Are you sure you want to Lock Employee?'
                  : 'Are you sure you want to Unlock Employee?'}
              </h3>

              <div className="flex justify-between gap-3 w-full">
                <button
                  onClick={handleDialogClose}
                  className="flex-1 border border-gray-200 rounded-lg h-10 bg-[#FFF8F3] text-gray-700 text-sm font-semibold hover:bg-orange-50"
                >
                  Cancel
                </button>
                <BtnPrimary
                  onClick={handleConfirmLock}
                  className="flex-1"
                  style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
                  disabled={lockLoading}
                >
                  {lockLoading ? 'Processing...' : 'Confirm'}
                </BtnPrimary>
              </div>
            </div>
          </Dialog>

          {/* ✅ Main Content */}
          {sessionView === "view" ? (
            <EmployeeSessionView />
          ) : (
            <>
              {/* Tabs */}
              <div className="flex gap-0 p-1 bg-[#FFF4E5] border border-[#FFF4E5] rounded-lg mb-6">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`flex-1 text-sm font-normal transition-colors py-2 px-3 rounded-md ${activeTab === 'overview'
                      ? 'text-gray-900 bg-white shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]'
                      : 'text-gray-600 bg-[#FFF4E5]'
                    }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('sessions')}
                  className={`flex-1 text-sm font-normal transition-colors py-2 px-3 rounded-md ${activeTab === 'sessions'
                      ? 'text-gray-900 bg-white shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]'
                      : 'text-gray-600 bg-[#FFF4E5]'
                    }`}
                >
                  Sessions
                </button>
              </div>

              {/* Conditional Rendering */}
              {activeTab === 'overview' && <OverViewEmployee coachData={displayData} loading={employeeDetailLoading} />}
              {activeTab === 'sessions' && <SessionList />}
            </>
          )}
        </div>
      </div>

      {/* ✅ NEW: Render the Image Preview Modal */}
      <ImagePreviewModal
        isOpen={imageModal.isOpen}
        onClose={closeImagePreview}
        imageUrl={imageModal.url}
        altText={imageModal.alt}
      />

    </div>
  );
};

export default EmployeeView;